import React, { useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Button } from '@mui/material';
import { useApiClient, useFetch, useFetchImmutable } from '@marageti/z4-lib';
import {
  AddedFailureReason,
  ScreeningResult,
  ScreeningStatus,
} from '@marageti/z4-sdk/lib/travel';
import {
  buildCorrectionPayload,
  commentsByReason,
  passedReasonsOf,
  resolveReasonSet,
  toFailureReasonChips,
} from './helpers';
import { ScreeningCriteriaMap } from './types';
import useScreeningCorrection from './use-screening-correction';

const useFailureReasons = (tripRequestId?: string, isScreened = false) => {
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);

  const screeningResult = useFetch<ScreeningResult>(
    apiClient.screeningResultsClient.getScreeningResult,
    [tripRequestId],
    !!tripRequestId,
    {
      shouldRetryOnError: (error: { code?: number }) => error.code !== 404,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  const { data: screeningCriteriaMap } = useFetchImmutable<ScreeningCriteriaMap>(
    apiClient.screeningCriteriaClient.getScreeningCriteriaMap,
    [],
    true,
  );

  const handleCorrectionLoadError = (retry: () => void) => {
    enqueueSnackbar('Unable to load saved failure reasons. Please try again.', {
      variant: 'default',
      action: (
        <Button color="inherit" onClick={retry} size="small">
          Retry
        </Button>
      ),
    });
  };

  const {
    status,
    data,
    mutate,
    saveScreeningCorrection,
  } = useScreeningCorrection(screeningResult.data, handleCorrectionLoadError);

  const isCorrectionLoading = status === 'loading';
  const fallbackReasons = screeningResult.data?.criteria
    ?.filter((criterion) => !criterion.passed)
    .map((criterion) => criterion.name) ?? [];
  const originalReasons = resolveReasonSet(
    isCorrectionLoading,
    data?.originalFailureReasons,
    fallbackReasons,
  );
  const effectiveReasons = resolveReasonSet(
    isCorrectionLoading,
    data?.effectiveFailureReasons,
    fallbackReasons,
  );
  const criteriaByName = useMemo(() => Object.fromEntries(
    (screeningResult.data?.criteria ?? []).map((criterion) => [criterion.name, criterion.result]),
  ), [screeningResult.data?.criteria]);

  const persistFailureReasons = async (
    nextEffective: string[],
    commentOverrides: Record<string, string | undefined> = {},
  ) => {
    if (status !== 'ready') {
      return false;
    }

    setIsSaving(true);
    try {
      const nextState = await saveScreeningCorrection(
        buildCorrectionPayload(originalReasons, nextEffective, {
          ...commentsByReason(data?.correction?.addedFailureReasons),
          ...commentOverrides,
        }),
      );
      await mutate(nextState, { revalidate: false });
      enqueueSnackbar('Failure reasons updated.', { variant: 'success' });
      return true;
    } catch {
      enqueueSnackbar('Unable to update failure reasons. Please try again.', { variant: 'default' });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveFailureReason = async (code: string) => {
    await persistFailureReasons(effectiveReasons.filter((reason) => reason !== code));
  };

  const handleRemoveAllFailureReasons = async () => {
    await persistFailureReasons([]);
  };

  const handleAddFailureReasons = async (drafts: AddedFailureReason[]) => {
    const nextEffective = [
      ...effectiveReasons,
      ...drafts.map((draft) => draft.reason).filter((code) => !effectiveReasons.includes(code)),
    ];
    return persistFailureReasons(
      nextEffective,
      Object.fromEntries(drafts.map((draft) => [draft.reason, draft.comment])),
    );
  };

  return {
    canEdit: status === 'ready'
      && !isScreened
      && screeningResult.data?.status !== ScreeningStatus.ScreeningProcessing,
    failureReasons: toFailureReasonChips(
      effectiveReasons,
      originalReasons,
      criteriaByName,
      commentsByReason(data?.correction?.addedFailureReasons),
    ),
    isLoading: screeningResult.isLoading || isCorrectionLoading,
    isSaving,
    passedReasons: passedReasonsOf(screeningResult.data?.criteria),
    screeningCriteriaMap,
    onAddFailureReasons: handleAddFailureReasons,
    onRemoveAllFailureReasons: handleRemoveAllFailureReasons,
    onRemoveFailureReason: handleRemoveFailureReason,
  };
};

export default useFailureReasons;
